import {useEffect, useState} from 'react'
import {Modal} from 'react-bootstrap'
import ReactSelect from 'react-select'
import Swal from 'sweetalert2'
import withReactContent from 'sweetalert2-react-content'

import {KTIcon} from '../../../../../../_metronic/helpers'
import {SelectStyles} from '../../../../../../components/Select'
import {useThemeMode} from '../../../../../../_metronic/partials'
import {ROLE} from '../../../../auth'
import {ROLE_NAME} from '../../../../../../constants'
import {generatePassword} from '../../../../../common/core/password'
import {addNewEmployee, attachMerchant} from '../../../../staff/api'
import {getLinkableUsers} from '../../MerchantsList/api'
import {LinkableUser} from '../../MerchantsList/types'

const AppSwal = withReactContent(Swal)

// С карточки мерчанта управляем только этими двумя ролями — операторы платежей мерчанта,
// матчинг-операторы и менеджеры мерчантов заводятся не здесь.
const OPERATOR_ROLES = [ROLE.ROLE_OPERATOR, ROLE.ROLE_SENIOR_OPERATOR]

type Mode = 'new' | 'existing'

interface ModalAddOperatorProps {
  merchantId: number
  merchantTitle: string
  onAdded: () => void
}

const errorText = (err: any): string => {
  const errors = err?.response?.data?.errors
  if (typeof errors === 'string') {
    return errors
  }
  if (errors && typeof errors === 'object') {
    const first = Object.values(errors)[0]
    if (typeof first === 'string') {
      return first
    }
  }
  if (err?.response?.status === 403) {
    return 'Недостаточно прав для этого мерчанта'
  }

  return 'Не удалось выполнить запрос'
}

export const ModalAddOperator = ({merchantId, merchantTitle, onAdded}: ModalAddOperatorProps) => {
  const {mode: theme} = useThemeMode()
  const [show, setShow] = useState(false)
  const [mode, setMode] = useState<Mode>('new')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [role, setRole] = useState<string>(ROLE.ROLE_OPERATOR)
  const [login, setLogin] = useState('')
  const [password, setPassword] = useState('')

  const [candidates, setCandidates] = useState<LinkableUser[]>([])
  const [loadingCandidates, setLoadingCandidates] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)

  // Список существующих грузим сразу при открытии модалки, чтобы переключение
  // режима не заставляло ждать запрос.
  const fetchCandidates = async () => {
    setLoadingCandidates(true)

    try {
      const {data} = await getLinkableUsers(merchantId)

      if (data.success && data.data) {
        setCandidates(data.data)
      }
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoadingCandidates(false)
    }
  }

  useEffect(() => {
    if (show) {
      fetchCandidates()
    }
  }, [show]) // eslint-disable-line react-hooks/exhaustive-deps

  const openModal = () => {
    setMode('new')
    setRole(ROLE.ROLE_OPERATOR)
    setLogin('')
    setPassword('')
    setSelectedUserId(null)
    setError(null)
    setShow(true)
  }

  const submitNew = async () => {
    const {data} = await addNewEmployee(login.trim(), password, role, null, merchantId)

    if (!data?.success) {
      throw new Error('bad response')
    }

    setShow(false)
    onAdded()

    AppSwal.fire({
      icon: 'success',
      title: 'Оператор создан',
      html: `Логин: <b>${login.trim()}</b><br/>Пароль: <b>${password}</b><br/><span class="text-muted">Пароль больше не будет показан — сохраните его.</span>`,
      confirmButtonText: 'Готово',
    })
  }

  const submitExisting = async () => {
    if (selectedUserId === null) {
      return
    }

    const {data} = await attachMerchant(selectedUserId, merchantId)

    if (!data?.success) {
      throw new Error('bad response')
    }

    setShow(false)
    onAdded()

    AppSwal.fire({
      icon: 'success',
      title: 'Оператор привязан',
      timer: 1500,
      showConfirmButton: false,
    })
  }

  const onSubmit = async () => {
    setLoading(true)
    setError(null)

    try {
      if (mode === 'new') {
        await submitNew()
      } else {
        await submitExisting()
      }
    } catch (err) {
      setError(errorText(err))
    } finally {
      setLoading(false)
    }
  }

  const submitDisabled =
    loading ||
    (mode === 'new' ? !login.trim() || password.length < 6 : selectedUserId === null)

  return (
    <>
      <button className='btn btn-sm btn-success' onClick={openModal}>
        <KTIcon iconName='plus' className='fs-2' />
        Добавить оператора
      </button>

      {/* Без aria-hidden: содержимое открытой модалки должно оставаться в дереве доступности */}
      <Modal
        dialogClassName='modal-dialog modal-dialog-centered mw-650px'
        show={show}
        backdrop={true}
      >
        <div className='modal-header'>
          <h2>Добавить оператора</h2>
          <div
            className='btn btn-sm btn-icon btn-active-color-primary'
            onClick={() => setShow(false)}
          >
            <KTIcon className='fs-1' iconName='cross' />
          </div>
        </div>

        <div className='modal-body py-lg-10 px-lg-10'>
          <ul className='nav nav-tabs nav-line-tabs mb-8 fs-6'>
            <li className='nav-item'>
              <button
                type='button'
                className={`nav-link ${mode === 'new' ? 'active' : ''}`}
                onClick={() => {
                  setMode('new')
                  setError(null)
                }}
              >
                Новый
              </button>
            </li>
            <li className='nav-item'>
              <button
                type='button'
                className={`nav-link ${mode === 'existing' ? 'active' : ''}`}
                onClick={() => {
                  setMode('existing')
                  setError(null)
                }}
              >
                Существующий
              </button>
            </li>
          </ul>

          <div className='row mb-6'>
            <label className='col-lg-4 col-form-label fw-bold fs-6'>Мерчант</label>
            <div className='col-lg-8'>
              <div className='form-control form-control-lg form-control-solid bg-light'>
                {merchantTitle}
              </div>
            </div>
          </div>

          {mode === 'new' ? (
            <>
              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Роль</label>
                <div className='col-lg-8'>
                  <select
                    className='form-select form-select-solid form-select-lg fw-bold'
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  >
                    {OPERATOR_ROLES.map((value) => (
                      <option value={value} key={value}>
                        {ROLE_NAME[value]}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Логин</label>
                <div className='col-lg-8'>
                  <input
                    type='text'
                    className='form-control form-control-lg form-control-solid'
                    placeholder='Логин'
                    value={login}
                    onChange={(e) => setLogin(e.target.value)}
                  />
                </div>
              </div>

              <div className='row mb-6'>
                <label className='col-lg-4 col-form-label required fw-bold fs-6'>Пароль</label>
                <div className='col-lg-8'>
                  <div className='input-group input-group-lg'>
                    <input
                      type='text'
                      autoComplete='off'
                      className='form-control form-control-lg form-control-solid'
                      placeholder='Не менее 6 символов'
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      type='button'
                      className='btn btn-light-primary'
                      title='Сгенерировать пароль'
                      onClick={() => setPassword(generatePassword())}
                    >
                      Сгенерировать
                    </button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className='row mb-6'>
              <label className='col-lg-4 col-form-label required fw-bold fs-6'>Оператор</label>
              <div className='col-lg-8'>
                {loadingCandidates ? (
                  <div className='form-control form-control-lg form-control-solid bg-light'>
                    Загрузка операторов...
                  </div>
                ) : (
                  <ReactSelect
                    options={candidates.map((user) => ({value: user.value, label: user.label}))}
                    value={
                      candidates
                        .filter((user) => user.value === selectedUserId)
                        .map((user) => ({value: user.value, label: user.label}))[0] ?? null
                    }
                    onChange={(opt: any) => setSelectedUserId(opt ? opt.value : null)}
                    placeholder={
                      candidates.length ? 'Выберите оператора' : 'Нет доступных операторов'
                    }
                    isClearable
                    styles={SelectStyles(theme)}
                  />
                )}
              </div>
            </div>
          )}

          {error && <div className='alert alert-danger'>{error}</div>}
        </div>

        <div className='card-footer d-flex justify-content-end py-6 px-9'>
          <button type='button' className='btn' onClick={() => setShow(false)}>
            Отмена
          </button>
          <button
            type='button'
            className='btn btn-primary'
            disabled={submitDisabled}
            onClick={onSubmit}
          >
            {loading ? 'Сохранение...' : mode === 'new' ? 'Создать' : 'Привязать'}
          </button>
        </div>
      </Modal>
    </>
  )
}
